import {
  type BatteryCellVoltages,
  type BatteryLiveState,
  getBatteryCellVoltages,
  getBatteryLiveState,
  sendBatteryCommand,
} from '$lib/powerhiveApi';
import { auth } from './auth.svelte';

let selectedBatteryId = $state<string | null>(null);
let selectedBatteryLabel = $state<string | null>(null);
let cellVoltages = $state<BatteryCellVoltages | null>(null);
let liveState = $state<BatteryLiveState | null>(null);
let loading = $state(false);
let error = $state<string | null>(null);
let commandInFlight = $state(false);
let commandResult = $state<CommandResult | null>(null);
/** Epoch ms of the last completed load, for the modal's freshness line. */
let updatedAt = $state<number | null>(null);

let abortController: AbortController | null = null;

export type BatteryCommand = 'MOS_ON' | 'MOS_OFF' | 'REBOOT';

interface CommandResult {
  ok: boolean;
  action: BatteryCommand;
  message: string;
}

export const batteryDiagnostics = {
  get selectedBatteryId() {
    return selectedBatteryId;
  },
  /** Human-facing physical-pack serial; API calls continue using batteryId. */
  get selectedBatteryLabel() {
    return selectedBatteryLabel;
  },
  get cellVoltages() {
    return cellVoltages;
  },
  get liveState() {
    return liveState;
  },
  get loading() {
    return loading;
  },
  get error() {
    return error;
  },
  get commandInFlight() {
    return commandInFlight;
  },
  get commandResult() {
    return commandResult;
  },
  get updatedAt() {
    return updatedAt;
  },
  clearCommandResult(): void {
    commandResult = null;
  },

  async inspect(batteryId: string, displayLabel?: string): Promise<void> {
    if (!batteryId) return;
    abortController?.abort();
    const ac = new AbortController();
    abortController = ac;

    selectedBatteryId = batteryId;
    selectedBatteryLabel = displayLabel ?? selectedBatteryLabel ?? batteryId;
    loading = true;
    error = null;
    commandResult = null;

    try {
      const [cellsResult, stateResult] = await Promise.allSettled([
        getBatteryCellVoltages(batteryId, ac.signal),
        getBatteryLiveState(batteryId, ac.signal),
      ]);

      if (ac.signal.aborted) return;

      if (cellsResult.status === 'fulfilled') {
        cellVoltages = cellsResult.value;
      } else {
        cellVoltages = null;
      }

      if (stateResult.status === 'fulfilled') {
        liveState = stateResult.value;
      } else {
        liveState = null;
      }

      if (cellsResult.status === 'rejected' && stateResult.status === 'rejected') {
        const err = cellsResult.reason;
        error = err instanceof Error ? err.message : String(err);
      } else {
        updatedAt = Date.now();
      }
    } catch (e) {
      if (!ac.signal.aborted) {
        error = e instanceof Error ? e.message : String(e);
      }
    } finally {
      if (abortController === ac) {
        loading = false;
        abortController = null;
      }
    }
  },

  /**
   * Dispatch a hardware command. Admin-only: the UI hides these controls, but
   * the store is reachable from `window.__batteryDiagnostics` and the console,
   * so it re-checks the role itself. The authoritative check is server-side in
   * the `battery-command` edge function — this is the belt-and-braces client
   * guard, mirroring UserAccessPanel's `onRoleChange`.
   */
  async executeCommand(action: BatteryCommand): Promise<boolean> {
    if (!selectedBatteryId) return false;
    if (!auth.roleReady || auth.role !== 'admin') {
      commandResult = {
        ok: false,
        action,
        message: 'Hardware commands require an admin role.',
      };
      return false;
    }
    commandInFlight = true;
    commandResult = null;
    try {
      await sendBatteryCommand(selectedBatteryId, action);
      commandResult = {
        ok: true,
        action,
        message: `${action} dispatched. Re-reading telemetry…`,
      };
      // Re-fetch telemetry after a short delay
      setTimeout(() => {
        if (selectedBatteryId)
          void batteryDiagnostics.inspect(selectedBatteryId, selectedBatteryLabel ?? undefined);
      }, 1500);
      return true;
    } catch (err) {
      commandResult = {
        ok: false,
        action,
        message: err instanceof Error ? err.message : String(err),
      };
      return false;
    } finally {
      commandInFlight = false;
    }
  },

  close(): void {
    abortController?.abort();
    abortController = null;
    selectedBatteryId = null;
    selectedBatteryLabel = null;
    cellVoltages = null;
    liveState = null;
    loading = false;
    error = null;
    commandResult = null;
    commandInFlight = false;
    updatedAt = null;
  },
};
